import _ from 'lodash';
import moment from 'moment';
import {
  createReducer,
  createFetchReducers,
  createFetchSelectors,
  createFetchActionForNamespace
} from 'utils/reducers';
import {attachUuidKey} from 'utils/common';
import {getToday, formatDate} from 'utils/datetime';
import {
  buildBookingTypeColumn,
  buildBookingTypeTableHeaderData,
  buildRoomStatusColumn,
  buildRoomStatusTableHeaderData,
  buildPhysicalBookingTypeColumn,
  buildPhysicalBookingTypeTableHeaderData,
  buildPhysicalRoomStatusColumn,
  buildPhysicalRoomStatusTableHeaderData,
  buildAvailableColumn,
  buildAvailableTableHeaderData
} from './utils';

export const UNITS_CHOICES = {
  PERCENT: 'percent',
  NUMBERS: 'numbers'
};

export const VIEW_CHOICES = {
  OCCUPIED: 'occupied',
  AVAILABLE: 'available'
};

export const OCCUPANCY_TYPE_CHOICES = {
  PHYSICAL_OCCUPANCY: 'physicalOccupancy',
  REVENUE_OCCUPANCY: 'revenueOccupancy'
};

export const OCCUPANCY_STATUSES = {
  AVAILABLE: 'available',
  BOOKED: 'booked',
  PHYSICAL_BOOKED: 'physicalBooked',
  CONFIRMED: 'confirmed',
  RESERVED: 'reserved',
  OCCUPIED_BY_EARLY_TERMINATION: 'occupiedByEt',
  OCCUPIED_BY_EARLY_CHECKOUT: 'occupiedByEc',
  OUT_OF_ORDER: 'outOfOrder',
  OUT_OF_ORDER_TURNAROUND: 'turnaround',
  OUT_OF_ORDER_MAINTENANCE: 'maintenance',
  OUT_OF_ORDER_OTHER: 'other',
  TOTAL_ROOMS: 'totalRooms',
  LONG_STAY_ROOMS: 'longStayRooms'
};

export const TOTAL = 'Total';

export const REPORT_SHAPE = {
  BOOKING_TYPE: {
    key: 'bookingType',
    label: 'Room Type', // Renamed to Room Type
    segments: [] // Since RoomTypeGroups for each location is different, these segments are generated from the response
  },
  ROOM_STATUS: {
    key: 'roomStatus',
    label: 'Room Status',
    segments: [
      {label: 'Confirmed', value: OCCUPANCY_STATUSES.CONFIRMED},
      {label: 'Reserved', value: OCCUPANCY_STATUSES.RESERVED},
      {
        label: 'Out of order',
        value: OCCUPANCY_STATUSES.OUT_OF_ORDER,
        nestedElements: [
          {
            label: 'Turnaround',
            value: OCCUPANCY_STATUSES.OUT_OF_ORDER_TURNAROUND
          },
          {
            label: 'Maintenance',
            value: OCCUPANCY_STATUSES.OUT_OF_ORDER_MAINTENANCE
          },
          {
            label: 'Other',
            value: OCCUPANCY_STATUSES.OUT_OF_ORDER_OTHER
          }
        ]
      },
      {
        label: 'Occupied By Early Termination',
        value: OCCUPANCY_STATUSES.OCCUPIED_BY_EARLY_TERMINATION
      },
      {
        label: 'Occupied By Early Checkout',
        value: OCCUPANCY_STATUSES.OCCUPIED_BY_EARLY_CHECKOUT
      }
    ]
  },
  ROOM_PURPOSE: {
    key: 'roomPurpose',
    label: 'Room Purpose',
    segments: [
      {
        label: 'Long Stay',
        value: 'long_stay'
      },
      {label: 'Corporate', value: 'corporate'},
      {
        label: 'Guest',
        value: 'guest'
      },
      {
        label: 'Extended Stay',
        value: 'extended_stay'
      },
      {
        label: 'Short stay',
        value: 'short_stay'
      }
    ]
  }
};

export const RESOLUTION_OPTIONS = {
  DAILY: {
    value: 'daily',
    label: 'Daily',
    defaultStartDate: getToday(),
    defaultEndDate: formatDate(moment().add(18, 'days'))
  },
  WEEKLY: {
    value: 'weekly',
    label: 'Weekly',
    defaultStartDate: getToday(),
    defaultEndDate: formatDate(moment().add(18, 'weeks'))
  },
  MONTHLY: {
    value: 'monthly',
    label: 'Monthly',
    defaultStartDate: getToday(),
    defaultEndDate: formatDate(moment().add(18, 'months'))
  }
};

const createFetchAction = createFetchActionForNamespace('occupancy');

export const fetchLiveAvailability = createFetchAction({
  name: 'FETCH_LIVE_AVAILABILITY',
  initialAction: ({locationRef, roomTypeGroup}) => ({
    payload: {locationRef, roomTypeGroup}
  }),
  stateKey: 'liveAvailability',
  defaultStateValue: {}
});

const liveAvailabilitySelector = createFetchSelectors(fetchLiveAvailability);
export const selectLiveAvailability = liveAvailabilitySelector.data;
export const selectLiveAvailabilityLoading = liveAvailabilitySelector.isLoading;

export const fetchOccupancyReportForPeriod = createFetchAction({
  name: 'FETCH_OCCUPANCY_REPORT_FOR_PERIOD',
  initialAction: ({locationRef, periodStart, periodEnd, resolution}) => ({
    payload: {locationRef, periodStart, periodEnd, resolution}
  }),
  stateKey: 'occupancyReport',
  defaultStateValue: {
    [VIEW_CHOICES.OCCUPIED]: {
      [OCCUPANCY_TYPE_CHOICES.REVENUE_OCCUPANCY]: {
        [REPORT_SHAPE.BOOKING_TYPE.key]: {
          elementsList: [],
          tableHeaderData: []
        },
        [REPORT_SHAPE.ROOM_STATUS.key]: {elementsList: [], tableHeaderData: []}
      },
      [OCCUPANCY_TYPE_CHOICES.PHYSICAL_OCCUPANCY]: {
        [REPORT_SHAPE.BOOKING_TYPE.key]: {
          elementsList: [],
          tableHeaderData: []
        },
        [REPORT_SHAPE.ROOM_STATUS.key]: {elementsList: [], tableHeaderData: []}
      }
    },
    [VIEW_CHOICES.AVAILABLE]: {elementsList: [], tableHeaderData: []}
  }
});

const occupancySelector = createFetchSelectors(fetchOccupancyReportForPeriod);

export const selectOccupancy = occupancySelector.data;
export const selectOccupancyLoading = occupancySelector.isLoading;
export const selectOccupancyError = occupancySelector.errors;

export const fetchOccupancyReportForRoomPurposes = createFetchAction({
  name: 'FETCH_OCCUPANCY_REPORT_FOR_ROOM_PURPOSES',
  initialAction: (
    {locationRef, periodStart, periodEnd, resolution},
    extraActions = []
  ) => ({
    payload: {
      locationRef,
      periodStart,
      periodEnd,
      resolution,
      extraActions
    }
  }),
  defaultStateValue: [],
  stateKey: 'occupancyReportForRoomPurposes'
});

const occupancyForRoomPurposesSelector = createFetchSelectors(
  fetchOccupancyReportForRoomPurposes
);

export const selectOccupancyForRoomPurposes =
  occupancyForRoomPurposesSelector.data;
export const selectOccupancyForRoomPurposesLoading =
  occupancyForRoomPurposesSelector.isLoading;
export const selectOccupancyForRoomPurposesError =
  occupancyForRoomPurposesSelector.errors;

const initialState = {};

const fetchOccupancyReportForPeriodReducer = createFetchReducers(
  fetchOccupancyReportForPeriod,
  (state, action) => ({
    ...state,
    [action.parent.stateKey]: {
      isLoading: false,
      data: {
        [VIEW_CHOICES.OCCUPIED]: {
          [OCCUPANCY_TYPE_CHOICES.REVENUE_OCCUPANCY]: {
            [REPORT_SHAPE.BOOKING_TYPE.key]: {
              elementsList: action.payload
                .map(buildBookingTypeColumn)
                .map(attachUuidKey),
              tableHeaderData: buildBookingTypeTableHeaderData(action.payload)
            },
            [REPORT_SHAPE.ROOM_STATUS.key]: {
              elementsList: action.payload
                .map(buildRoomStatusColumn)
                .map(attachUuidKey),
              tableHeaderData: buildRoomStatusTableHeaderData()
            }
          },
          [OCCUPANCY_TYPE_CHOICES.PHYSICAL_OCCUPANCY]: {
            [REPORT_SHAPE.BOOKING_TYPE.key]: {
              elementsList: action.payload
                .map(buildPhysicalBookingTypeColumn)
                .map(attachUuidKey),
              tableHeaderData: buildPhysicalBookingTypeTableHeaderData(
                action.payload
              )
            },
            [REPORT_SHAPE.ROOM_STATUS.key]: {
              elementsList: action.payload
                .map(buildPhysicalRoomStatusColumn)
                .map(attachUuidKey),
              tableHeaderData: buildPhysicalRoomStatusTableHeaderData()
            }
          }
        },
        [VIEW_CHOICES.AVAILABLE]: {
          elementsList: action.payload
            .map(buildAvailableColumn)
            .map(attachUuidKey),
          tableHeaderData: buildAvailableTableHeaderData(action.payload)
        }
      }
    }
  })
);

const fetchReducers = createReducer(initialState, {
  ...fetchOccupancyReportForPeriodReducer,
  ...createFetchReducers(fetchLiveAvailability),
  ...createFetchReducers(fetchOccupancyReportForRoomPurposes)
});

const reducer = (state = initialState, action) => fetchReducers(state, action);

export default reducer;
export {default as occupancySaga} from './saga';
